import type { IntlShape } from 'react-intl'
import { isChatHandoffUnavailableResponse } from 'tripPlanner/api/v3'

/**
 * Get a user-facing error message based on the HTTP status code
 * returned when loading a chat fails.
 */
export function getChatErrorMessage(intl: IntlShape, errorStatus: number | undefined): string {
  if (errorStatus === 403) {
    return intl.formatMessage({
      id: 'rwKIu5ZmMXZo8aQcE1rM',
      defaultMessage: 'You don\u2019t have access to this chat.',
      description: 'Error message when user lacks permission to view a trip planner chat',
    })
  }
  if (errorStatus === 404) {
    return intl.formatMessage({
      id: 'AWkEjshiaVtIX3WvWZa8',
      defaultMessage: 'This chat no longer exists.',
      description: 'Error message when a trip planner chat has been deleted',
    })
  }
  if (errorStatus === 401) {
    return intl.formatMessage({
      id: 'WlGNGf03-MFDo1bDmqOk',
      defaultMessage: 'Please sign in to view this chat.',
      description: 'Error message when unauthenticated user tries to view a trip planner chat',
    })
  }
  return intl.formatMessage({
    id: 'jz_sicOJsv-O15uHQWVB',
    defaultMessage: 'This chat could not be loaded.',
    description: 'Generic error message when a trip planner chat fails to load',
  })
}

export function getChatErrorHeading(intl: IntlShape): string {
  return intl.formatMessage({
    id: '7UwMzR3Ox-IosoF7mN3u',
    defaultMessage: 'Chat unavailable',
    description: 'Snackbar heading when a trip planner chat cannot be loaded',
  })
}

interface ChatHandoffErrorCopy {
  heading: string
  message: string
}

function isBadRequestResponse(error: unknown): boolean {
  return error !== null && typeof error === 'object' && 'status' in error && error.status === 400
}

export function getChatHandoffErrorCopy(intl: IntlShape, error: unknown): ChatHandoffErrorCopy {
  if (isChatHandoffUnavailableResponse(error) && error.reason === 'other_owner') {
    return {
      heading: intl.formatMessage({
        id: 'dWQY3u_72ba_OmopIKm4',
        defaultMessage: 'This link was already opened',
        description: 'Snackbar heading when a WhatsApp link to continue a trip planner chat on the website was already opened on another device, browser or account',
      }),
      message: intl.formatMessage({
        id: 'PKOv9woq_8k0EU5nQ8rX',
        defaultMessage: 'It was opened on another device or browser. Sign in there to find your chat, or ask us on WhatsApp for a new link.',
        description: 'Snackbar message when a WhatsApp link to continue a trip planner chat was already opened on another device, browser or account',
      }),
    }
  }
  if (isChatHandoffUnavailableResponse(error) || isBadRequestResponse(error)) {
    return {
      heading: intl.formatMessage({
        id: 'ti8XJeh7odpoDbKqCc6p',
        defaultMessage: 'This link has expired',
        description: 'Snackbar heading when a WhatsApp link to continue a trip planner chat on the website has expired or was already used',
      }),
      message: intl.formatMessage({
        id: 'jvPOVUWsiLWS082pSK1L',
        defaultMessage: 'Ask us on WhatsApp for a new link to continue your chat here.',
        description: 'Snackbar message telling the traveller to request a fresh link in WhatsApp after their link to continue the chat on the website expired',
      }),
    }
  }
  return {
    heading: getChatErrorHeading(intl),
    message: intl.formatMessage({
      id: 'Fp138Fe0o7d__LuT84Y_',
      defaultMessage: 'We couldn’t bring your WhatsApp chat across. Please open the link again.',
      description: 'Snackbar message when continuing a WhatsApp trip planner chat on the website fails for a temporary reason and the link can be retried',
    }),
  }
}
